import { useRef, useEffect } from 'react';

// To add more logos, simply append to this array.
const CLIENT_LOGOS: { src: string; alt: string }[] = [
  { src: '/static/client_logos/Alliance-Logo.png', alt: 'Alliance Construction Solutions' },
  { src: '/static/client_logos/berg_light.png', alt: 'Berg Electric' },
  { src: '/static/client_logos/bristlecone_light.png', alt: 'Bristlecone' },
  { src: '/static/client_logos/catamount_light.png', alt: 'Catamount' },
  { src: '/static/client_logos/deneuve_light.png', alt: 'DeNeuve Construction' },
  { src: '/static/client_logos/greystar_white.png', alt: 'Greystar' },
  { src: '/static/client_logos/Harvey-Cleary.png', alt: 'Harvey Cleary' },
  { src: '/static/client_logos/holland_light.png', alt: 'Holland Construction' },
  { src: '/static/client_logos/milender_light.png', alt: 'Milender White' },
  { src: '/static/client_logos/mpc_light.png', alt: 'MPC' },
  { src: '/static/client_logos/rec_light.png', alt: 'Roberts Electrical Contracting' },
  { src: '/static/client_logos/Screenshot_2025-08-21_at_3.10.58PM.png', alt: 'Dominion' },
  { src: '/static/client_logos/symmetry_light.png', alt: 'The Symmetry Companies' },
  { src: '/static/client_logos/vertix_light.png', alt: 'Vertix' },
];

const STATS = [
  { value: '10M+', label: 'sq ft documented' },
  { value: '9+', label: 'years of experience' },
  { value: '150+', label: 'firms served' },
];

const SocialProofBar: React.FC = () => {
  const trackRef = useRef<HTMLDivElement>(null);

  useEffect(() => {
    const track = trackRef.current;
    if (!track) return;

    let animFrame: number;
    let pos = 0;
    const speed = 0.75; // px per frame — adjust to taste

    const animate = () => {
      pos += speed;
      // Each logo slot is 160px wide (w-40) + 32px gap = 192px per item
      const singleWidth = track.scrollWidth / 2;
      if (pos >= singleWidth) pos = 0;
      track.style.transform = `translateX(-${pos}px)`;
      animFrame = requestAnimationFrame(animate);
    };

    animFrame = requestAnimationFrame(animate);

    const pause = () => cancelAnimationFrame(animFrame);
    const resume = () => { animFrame = requestAnimationFrame(animate); };

    track.parentElement?.addEventListener('mouseenter', pause);
    track.parentElement?.addEventListener('mouseleave', resume);

    return () => {
      cancelAnimationFrame(animFrame);
      track.parentElement?.removeEventListener('mouseenter', pause);
      track.parentElement?.removeEventListener('mouseleave', resume);
    };
  }, []);

  // Duplicate logos for seamless infinite loop
  const logos = [...CLIENT_LOGOS, ...CLIENT_LOGOS];

  return (
    <div className="relative z-10">
      {/* Navy carousel strip */}
      <div className="bg-maintenance-navy border-t border-b border-gray-200">
        {/* Label */}
        <div className="pt-8 pb-6 text-center px-4">
          <p className="text-gray-400 text-xs uppercase tracking-widest font-semibold">
            Trusted by leading construction firms
          </p>
        </div>

        {/* Scrolling carousel */}
        <div className="overflow-hidden pb-8" aria-label="Client logos">
          <div ref={trackRef} className="flex gap-12 w-max">
            {logos.map((logo, i) => (
              <div
                key={i}
                className="w-40 h-16 shrink-0 flex items-center justify-center"
              >
                <img
                  src={logo.src}
                  alt={logo.alt}
                  className="max-h-12 max-w-full object-contain select-none"
                  draggable={false}
                />
              </div>
            ))}
          </div>
        </div>
      </div>

      {/* Dark stat counters strip */}
      <div className="bg-maintenance-navy border-b border-white/10">
        <div className="container mx-auto px-4 md:px-6 py-8">
          <div className="grid grid-cols-3 gap-4 md:gap-8 max-w-2xl mx-auto">
            {STATS.map((stat) => (
              <div key={stat.label} className="text-center">
                <div className="text-3xl sm:text-4xl md:text-5xl font-bold text-white mb-1">
                  {stat.value}
                </div>
                <div className="text-white/50 text-xs sm:text-sm uppercase tracking-wide">
                  {stat.label}
                </div>
              </div>
            ))}
          </div>
        </div>
      </div>
    </div>
  );
};

export default SocialProofBar;
